import React from "react";
import { Link } from "react-router-dom";

// Guía de configuración de la institución/escuela — pensada para un
// admin que configura la plataforma por primera vez. El orden de los
// pasos sigue las dependencias reales entre los datos (verificado
// revisando qué información consulta cada formulario): un curso
// necesita que exista un grado, una asignatura necesita un área
// académica, la organización académica necesita que todo lo anterior
// ya exista, etc.
const PASOS = [
  {
    numero: 1,
    icono: "fa-calendar-alt",
    titulo: "Año escolar",
    descripcion:
      "Crea el año escolar activo (ej. 2026). Todo lo demás —períodos, cursos, calificaciones— se organiza alrededor de este año.",
    tab: "añosEscolares",
  },
  {
    numero: 2,
    icono: "fa-atom",
    titulo: "Áreas académicas",
    descripcion:
      "Define las grandes áreas del conocimiento (Matemáticas, Ciencias, Humanidades...). Las asignaturas y dimensiones se agrupan bajo estas áreas.",
    tab: "areasAcademicas",
  },
  {
    numero: 3,
    icono: "fa-shapes",
    titulo: "Dimensiones",
    descripcion:
      "Si tu institución evalúa por dimensiones (común en preescolar o currículo por competencias), defínelas aquí, asociadas a un área académica.",
    tab: "dimensionAcademica",
    opcional: true,
  },
  {
    numero: 4,
    icono: "fa-book",
    titulo: "Asignaturas",
    descripcion:
      "Crea las materias que se dictan (Matemáticas, Español, Inglés...), cada una asociada a un área académica ya creada.",
    tab: "asignaturas",
  },
  {
    numero: 5,
    icono: "fa-graduation-cap",
    titulo: "Grados",
    descripcion:
      "Define los grados o niveles (Transición, 1°, 2°...) junto con su jornada y nivel educativo.",
    tab: "grados",
  },
  {
    numero: 6,
    icono: "fa-clock",
    titulo: "Períodos académicos",
    descripcion:
      "Define los cortes de calificación del año escolar (Período 1, 2, 3, 4), con sus fechas de inicio y cierre.",
    tab: "periodos",
  },
  {
    numero: 7,
    icono: "fa-book-open",
    titulo: "Cursos",
    descripcion:
      'Crea los cursos o grupos concretos (ej. "5°A"), cada uno asociado a un grado ya creado.',
    tab: "cursos",
  },
  {
    numero: 8,
    icono: "fa-chalkboard-teacher",
    titulo: "Organización académica",
    descripcion:
      "Asigna qué docente dicta cada asignatura en cada curso. Es el último paso: necesita que grados, cursos, asignaturas y docentes ya existan.",
    tab: "organizacionAcademica",
    final: true,
  },
];

const PASO_OPCIONAL = {
  icono: "fa-calendar-day",
  titulo: "Horario",
  descripcion:
    "Una vez tengas cursos, asignaturas y docentes asignados, configura aquí el horario de clases.",
  tab: "Horario",
};

const GuiaConfiguracion = () => {
  return (
    <div className="container-fluid p-0">
      {/* Encabezado */}
      <div
        className="rounded-4 p-4 p-md-5 mb-4 text-white"
        style={{
          background:
            "linear-gradient(135deg, var(--ge-primary, #3b6ff6) 0%, var(--ge-primary-hover, #2c5ce0) 55%, var(--ge-accent, #16181d) 100%)",
        }}
      >
        <span className="badge bg-light text-dark mb-2">Primeros pasos</span>
        <h1 className="h3 mb-2">Guía de configuración de tu institución</h1>
        <p className="mb-0" style={{ opacity: 0.9, maxWidth: "640px" }}>
          Sigue estos pasos <strong>en orden</strong> la primera vez que configures la
          plataforma. Cada paso necesita que el anterior ya exista — por ejemplo, no
          puedes crear un curso sin antes tener un grado.
        </p>
      </div>

      {/* Pasos */}
      <div className="d-flex flex-column gap-3">
        {PASOS.map((paso) => (
          <div className="card shadow-sm" key={paso.numero}>
            <div className="card-body d-flex align-items-start gap-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: "2.75rem",
                  height: "2.75rem",
                  background: "var(--ge-primary-light, #eaf0fe)",
                  color: "var(--ge-primary, #3b6ff6)",
                  fontWeight: 700,
                }}
              >
                {paso.numero}
              </div>

              <div className="flex-grow-1">
                <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                  <i className={`fas ${paso.icono} text-muted`}></i>
                  <h5 className="mb-0">{paso.titulo}</h5>
                  {paso.opcional && (
                    <span className="badge badge-subtle-secondary">Opcional</span>
                  )}
                  {paso.final && (
                    <span className="badge badge-subtle-warning">Último paso</span>
                  )}
                </div>
                <p className="text-muted mb-0">{paso.descripcion}</p>
              </div>

              <Link
                to={`/ConfiguracionAcademica?tab=${paso.tab}`}
                className="btn btn-outline-primary flex-shrink-0"
              >
                Ir a configurar <i className="fas fa-arrow-right ms-1"></i>
              </Link>
            </div>
          </div>
        ))}

        {/* Paso opcional aparte, después de que todo lo esencial esté listo */}
        <div className="card shadow-sm border-dashed">
          <div className="card-body d-flex align-items-start gap-3">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: "2.75rem",
                height: "2.75rem",
                background: "var(--ge-bg-subtle, #f0f1f5)",
                color: "var(--ge-text-muted, #8a8f98)",
              }}
            >
              <i className={`fas ${PASO_OPCIONAL.icono}`}></i>
            </div>
            <div className="flex-grow-1">
              <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                <h5 className="mb-0">{PASO_OPCIONAL.titulo}</h5>
                <span className="badge badge-subtle-secondary">Opcional</span>
              </div>
              <p className="text-muted mb-0">{PASO_OPCIONAL.descripcion}</p>
            </div>
            <Link
              to={`/ConfiguracionAcademica?tab=${PASO_OPCIONAL.tab}`}
              className="btn btn-outline-primary flex-shrink-0"
            >
              Ir a configurar <i className="fas fa-arrow-right ms-1"></i>
            </Link>
          </div>
        </div>
      </div>

      {/* Nota final */}
      <div className="alert alert-info mt-4 d-flex align-items-start gap-2">
        <i className="fas fa-circle-info mt-1"></i>
        <div>
          <strong>¿Ya configuraste todo?</strong> Puedes volver a esta guía cuando
          quieras desde el menú lateral — es útil también cuando agregues un grado o
          curso nuevo a mitad de año.
        </div>
      </div>
    </div>
  );
};

export default GuiaConfiguracion;
